// frontend/src/pages/BookListPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader2 } from 'lucide-react';
import { fetchBooks, fetchBookCategories } from '../services/bookService';
import BookCard from '../components/library/BookCard';
import FilterPanel from '../components/library/FilterPanel';

const TAG_TITLE_MAP = {
    'Author': 'تألیفات',
    'Translation': 'ترجمه‌ها',
    'Introduction': 'مقدمه‌ها',
    'Editor': 'ویراسته‌ها',
    'mostafa-malekian': 'تمام آثار مصطفی ملکیان',
    'Spinoza': 'کتاب‌های مرتبط با اسپینوزا',
    'Stoics': 'رواقیان',
    'Stoicism': 'رواقی‌گری',
};

const getDynamicTitle = (filters, apiTitle = null, categoryTitleMap = {}) => {
    if (filters.search) {
        return `لیست کامل جستجوی "${decodeURIComponent(filters.search)}"`;
    }
    if (filters.author__name) {
        return `لیست کامل آثار نویسنده "${decodeURIComponent(filters.author__name)}"`;
    }
    if (filters.translator__name) {
        return `لیست کامل ترجمه‌های "${decodeURIComponent(filters.translator__name)}"`;
    }
    if (filters.publisher__title) {
        return `لیست کامل نشر "${decodeURIComponent(filters.publisher__title)}"`;
    }
    if (filters.tags__slug) {
        const tagSlug = filters.tags__slug;
        const mapped = TAG_TITLE_MAP[tagSlug];
        if (mapped) return `لیست کامل ${mapped}`;
        return `لیست کامل ${tagSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`;
    }
    if (filters.category__slug) {
        if (apiTitle) return `لیست کامل ${apiTitle}`;
        const slug = filters.category__slug;
        const titleFromMap = categoryTitleMap[slug];
        if (titleFromMap) return `لیست کامل ${titleFromMap}`;
        return `لیست کامل ${slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`;
    }
    return 'لیست کامل کتاب‌ها';
};

const SUBCOLOR_PALETTE = [
    '#10b981', '#14b8a6', '#0891b2', '#0ea5e9',
    '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
];

const adjustFilters = (filters) => {
    const adjusted = { ...filters };
    if (adjusted.publisher__title) {
        adjusted.search = adjusted.publisher__title;
        delete adjusted.publisher__title;
    }
    if (adjusted.publisher__title__icontains) {
        delete adjusted.publisher__title__icontains;
    }
    return adjusted;
};

const BookListPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [books, setBooks] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [loadingSub, setLoadingSub] = useState(false);
    const [pageTitleInfo, setPageTitleInfo] = useState({ title: 'لیست کامل کتاب‌ها', categoryTitle: null });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [categoryTitleMap, setCategoryTitleMap] = useState({});

    const currentFilters = useMemo(() => Object.fromEntries(searchParams.entries()), [searchParams]);

    const setFilter = useCallback((filters) => {
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value.trim()) newParams.set(key, value);
                else newParams.delete(key);
            });
            return newParams;
        });
    }, [setSearchParams]);

    const clearFilter = useCallback((key) => {
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            if (key) newParams.delete(key);
            else {
                Array.from(newParams.keys()).forEach(k => {
                    if (k !== 'ordering') newParams.delete(k);
                });
            }
            return newParams;
        });
    }, [setSearchParams]);

    useEffect(() => {
        const loadSubcategories = async () => {
            const categorySlug = currentFilters['category__slug'];
            if (!categorySlug) {
                setSubcategories([]);
                return;
            }
            setLoadingSub(true);
            try {
                const response = await fetchBookCategories();
                let allCategories = Array.isArray(response.data) ? response.data : response.data?.results || [];
                const map = {};
                allCategories.forEach(cat => { map[cat.slug] = cat.title || cat.name; });
                setCategoryTitleMap(map);

                const currentCategory = allCategories.find(cat => cat.slug === categorySlug);
                if (currentCategory && currentCategory.id) {
                    setSubcategories(allCategories.filter(cat => cat.parent === currentCategory.id));
                } else {
                    setSubcategories([]);
                }
            } catch {
                setSubcategories([]);
            } finally {
                setLoadingSub(false);
            }
        };
        loadSubcategories();
    }, [currentFilters['category__slug']]);

    const fetchAllBooks = useCallback(async (filters) => {
        let allResults = [];
        let nextUrl = null;
        const firstResponse = await fetchBooks({ ...adjustFilters(filters), limit: 1000 });
        allResults = firstResponse.data?.results || [];
        nextUrl = firstResponse.data?.next;
        while (nextUrl) {
            const url = new URL(nextUrl);
            const params = Object.fromEntries(url.searchParams.entries());
            const response = await fetchBooks(params);
            allResults = [...allResults, ...(response.data?.results || [])];
            nextUrl = response.data?.next;
        }
        return allResults;
    }, []);

    const loadBooks = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const categorySlug = currentFilters['category__slug'];
            const adjFilters = adjustFilters(currentFilters);
            let allBooks = [];

            if (categorySlug && subcategories.length > 0) {
                const parentBooks = await fetchAllBooks(adjFilters);
                const subBooksArrays = await Promise.all(
                    subcategories.map(sub => fetchAllBooks({ ...adjFilters, category__slug: sub.slug }))
                );
                allBooks = parentBooks.concat(...subBooksArrays);
                const uniqueMap = new Map(allBooks.map(b => [b.id, b]));
                setBooks(Array.from(uniqueMap.values()));
            } else {
                allBooks = await fetchAllBooks(adjFilters);
                setBooks(allBooks);
            }

            let apiTitle = null;
            if (categorySlug) {
                try {
                    const parentResponse = await fetchBooks({ ...adjFilters, limit: 1 });
                    apiTitle = parentResponse.data?.title;
                } catch { /* ignore */ }
            }
            setPageTitleInfo({
                title: getDynamicTitle(currentFilters, apiTitle, categoryTitleMap),
                categoryTitle: apiTitle || currentFilters.category__slug || null
            });
        } catch (err) {
            setError(err.message || "خطای نامشخص در واکشی داده.");
            setBooks([]);
        } finally {
            setLoading(false);
        }
    }, [currentFilters, subcategories, fetchAllBooks, categoryTitleMap]);

    useEffect(() => { loadBooks(); }, [loadBooks]);

    const defaultOgImage = 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/og-image.jpg';
    const metaTitle = `${pageTitleInfo.title} | فرزانه`;
    const metaDescription = `مشاهده و جستجوی ${pageTitleInfo.title} در وب‌اپلیکیشن فرزانه. شامل آثار مصطفی ملکیان و سایر نویسندگان در حوزه فلسفه، روانشناسی و هنر زندگی.`;

    if (loading) {
        return (
            <div className="flex justify-center items-center p-20 min-h-[50vh]">
                <Loader2 size={40} className="animate-spin text-indigo-600" />
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:image" content={defaultOgImage} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={metaTitle} />
                <meta name="twitter:description" content={metaDescription} />
                <meta name="twitter:image" content={defaultOgImage} />
            </Helmet>

            <div className="p-2 md:p-4 min-h-screen rtl text-right">
                <div className="max-w-7xl mx-auto">
                    <div className="mb-6">
                        <FilterPanel
                            currentFilters={currentFilters}
                            setFilter={setFilter}
                            clearFilter={clearFilter}
                            categoryTitle={pageTitleInfo.categoryTitle}
                        />
                    </div>

                    <h1 className="text-[14px] text-gray-600 font-black mb-6 border-b pb-2">
                        {pageTitleInfo.title}
                    </h1>

                    {subcategories.length > 0 && (
                        <div className="mb-8">
                            <div className="flex flex-wrap gap-1">
                                {subcategories.slice(0, 4).map((sub, idx) => (
                                    <Link
                                        key={sub.slug}
                                        to={`/books?category__slug=${sub.slug}`}
                                        className="px-2 py-2 rounded-lg border text-[11px] font-light transition-colors hover:bg-gray-50"
                                        style={{ borderColor: SUBCOLOR_PALETTE[idx % SUBCOLOR_PALETTE.length], color: SUBCOLOR_PALETTE[idx % SUBCOLOR_PALETTE.length], backgroundColor: 'white' }}
                                    >
                                        {sub.name || sub.title}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {error ? (
                        <div className="bg-white p-10 rounded-xl shadow-lg border border-red-300 rtl text-right">
                            <p className="text-[11px] font-thin text-red-600">خطا در بارگذاری: {error}</p>
                        </div>
                    ) : books.length === 0 ? (
                        <div className="bg-white p-10 rounded-xl shadow-lg border border-red-300 rtl text-right">
                            <p className="text-[11px] font-thin text-red-600">متأسفانه کتابی با این فیلترها یافت نشد.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {books.map((book) => (
                                <BookCard key={book.slug} book={book} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default BookListPage;